import { useContext, useState, useCallback, useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import H5 from 'UIComponents/elements/headings/H5';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIButton from 'UIComponents/button/UIButton';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { SupportLinkKeys } from 'automation-ui-usage-tracking/SupportLinks';
import DocButton from 'automation-ui-usage-tracking/components/DocButton';
import StyledOperationsHubOnboardingOverviewMenuButtons from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingOverviewMenuButtons';
import { StyledOperationsHubOnboardingOverviewMenuButton, StyledOperationsHubOnboardingOverviewMenuButtonUIIllustration, StyledOperationsHubOnboardingOverviewMenuButtonTitleDiv } from 'automation-ui-management/components/operations-hub-onboarding/StyledOperationsHubOnboardingOverviewMenuButton';
import { Flex } from 'foundations-components/components/Flex';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const pages = [{
  id: 'data-sync',
  illustration: 'operations-data-format',
  title: 'automation-ui-management.operations-hub-onboarding.pages.data-sync.title',
  description: 'automation-ui-management.operations-hub-onboarding.pages.data-sync.description_jsx',
  learnMore: 'automation-ui-management.operations-hub-onboarding.pages.data-sync.learnMoreLink_jsx',
  supportKey1: SupportLinkKeys.DATA_QUALITY
}, {
  id: 'format-data',
  illustration: 'workflows',
  title: 'automation-ui-management.operations-hub-onboarding.pages.format-data.title',
  description: 'automation-ui-management.operations-hub-onboarding.pages.format-data.description_jsx',
  learnMore: 'automation-ui-management.operations-hub-onboarding.pages.format-data.learnMoreLink_jsx',
  supportKey1: SupportLinkKeys.FORMAT_DATA_WORKFLOW_ACTION
}, {
  id: 'programmable-automation',
  illustration: 'api',
  title: 'automation-ui-management.operations-hub-onboarding.pages.programmable-automation.title',
  description: 'automation-ui-management.operations-hub-onboarding.pages.programmable-automation.description_jsx',
  learnMore: 'automation-ui-management.operations-hub-onboarding.pages.programmable-automation.learnMoreLink_jsx',
  supportKey1: SupportLinkKeys.CUSTOM_CODE_HELP,
  supportKey2: SupportLinkKeys.LEARN_ABOUT_WEBHOOKS
}, {
  id: 'scheduled-triggers',
  illustration: 'schedule',
  title: 'automation-ui-management.operations-hub-onboarding.pages.scheduled-triggers.title',
  description: 'automation-ui-management.operations-hub-onboarding.pages.scheduled-triggers.description_jsx',
  learnMore: 'automation-ui-management.operations-hub-onboarding.pages.scheduled-triggers.learnMoreLink_jsx',
  supportKey1: SupportLinkKeys.SCHEDULED_TRIGGERS
}];
export default function OperationsHubOnboardingContent() {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const [pageIndex, setPageIndex] = useState(-1);
  const handleStartButtonClick = useCallback(() => setPageIndex(0), [setPageIndex]);
  const handlePreviousButtonClick = useCallback(() => setPageIndex(oldIndex => oldIndex - 1), [setPageIndex]);
  const handleNextButtonClick = useCallback(() => setPageIndex(oldIndex => oldIndex + 1), [setPageIndex]);
  const showLandingPage = pageIndex < 0 || pageIndex >= pages.length;
  const activePage = !showLandingPage && pages[pageIndex];
  useEffect(() => {
    if (activePage) {
      trackEvent(usageTracker, Trackables.VIEWED_OPERATIONS_HUB_ONBOARDING_PAGE, {
        page: activePage.id
      });
    }
  }, [activePage, usageTracker]);
  if (!activePage) {
    return /*#__PURE__*/_jsxs(Flex, {
      as: "div",
      flexDirection: "column",
      flexGrow: 1,
      padding: "400",
      children: [/*#__PURE__*/_jsx(StyledOperationsHubOnboardingOverviewMenuButtons, {
        children: pages.map(({
          id,
          title,
          illustration
        }, index) => /*#__PURE__*/_jsxs(StyledOperationsHubOnboardingOverviewMenuButton, {
          onClick: () => setPageIndex(index),
          children: [/*#__PURE__*/_jsx(StyledOperationsHubOnboardingOverviewMenuButtonUIIllustration, {
            name: illustration,
            width: 56,
            height: 56
          }), /*#__PURE__*/_jsx(StyledOperationsHubOnboardingOverviewMenuButtonTitleDiv, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: title
            })
          })]
        }, id))
      }), /*#__PURE__*/_jsxs(UIButton, {
        use: "link",
        className: "m-top-3",
        onClick: handleStartButtonClick,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.operations-hub-onboarding.startLink"
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: "right"
        })]
      })]
    });
  } else {
    const {
      title,
      illustration,
      description,
      supportKey1,
      supportKey2,
      learnMore
    } = activePage;
    let supportLinks;
    if (supportKey1) {
      supportLinks = {
        DocButton: ({
          children
        }) => /*#__PURE__*/_jsx(DocButton, {
          supportKey: supportKey1,
          children: children
        })
      };
    }
    if (supportKey2) {
      supportLinks = Object.assign({}, supportLinks, {
        DocButton2: ({
          children
        }) => /*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(DocButton, {
            supportKey: supportKey2,
            children: children
          })
        })
      });
    }
    return /*#__PURE__*/_jsxs(View, {
      paddingBlockStart: "500",
      paddingBlockEnd: "200",
      as: "div",
      children: [/*#__PURE__*/_jsxs(Flex, {
        as: "div",
        flexGrow: 1,
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(Box, {
          marginInline: "300",
          alignSelf: "center",
          shrink: 0,
          children: /*#__PURE__*/_jsx(IconButton, {
            onClick: handlePreviousButtonClick,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "left"
            })
          })
        }), /*#__PURE__*/_jsx(Box, {
          marginInlineEnd: "300",
          shrink: 0,
          children: /*#__PURE__*/_jsx(Illustration, {
            name: illustration,
            height: 119,
            width: 119
          })
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(H5, {
            "aria-level": 3,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: title
            })
          }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: description,
            elements: {
              Bold: 'strong'
            }
          })]
        }), /*#__PURE__*/_jsx(Box, {
          marginInline: "300",
          alignSelf: "center",
          shrink: 0,
          children: /*#__PURE__*/_jsx(IconButton, {
            onClick: handleNextButtonClick,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "right"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(View, {
        marginInline: "700",
        marginBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: learnMore,
          elements: supportLinks
        })
      })]
    });
  }
}